import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminListCollections, adminUploadImage,
} from "@/lib/api";
import { RECIPIENTS, AVAILABILITY, BADGES } from "@/data/products";
import AdminHeader from "@/components/admin/AdminHeader";

const slugify = (s) => (s || "").toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const emptyForm = {
  id: null, name: "", slug: "", collection_slug: "",
  short_description: "", price: 0, mrp: "", images: [], recipient: [], availability: "ready-to-dispatch",
  moq: 50, contents: [{ name: "", description: "", icon_url: null }], box_contents_label: "", badge: "",
  featured: false, popularity: 50,
  meta_title: "", meta_description: "", status: "draft",
};

function assetUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function AdminProductsPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try {
      const [prods, cols] = await Promise.all([adminListProducts(), adminListCollections()]);
      setItems(prods);
      setCollections(cols);
    } catch { toast.error("Failed to load products"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => {
    const defaultCol = collections.find((c) => c.slug === "diwali-collection") || collections[0];
    setEditing({ ...emptyForm, collection_slug: defaultCol?.slug || "" });
  };
  const startEdit = (p) => setEditing({
    ...emptyForm, ...p,
    badge: p.badge || "",
    contents: p.contents && p.contents.length ? p.contents : [{ name: "", description: "", icon_url: null }],
  });
  const cancel = () => setEditing(null);

  const doSave = async (statusOverride) => {
    if (!editing.name.trim()) { toast.error("Product name required"); return; }
    if (!editing.collection_slug) { toast.error("Please assign a collection"); return; }
    const payload = {
      name: editing.name.trim(),
      slug: (editing.slug || slugify(editing.name)).trim(),
      collection_slug: editing.collection_slug || null,
      short_description: editing.short_description || "",
      price: Number(editing.price) || 0,
      mrp: editing.mrp === "" || editing.mrp == null ? null : Number(editing.mrp),
      images: editing.images.filter(Boolean),
      recipient: editing.recipient,
      availability: editing.availability,
      moq: Number(editing.moq) || 50,
      contents: editing.contents.filter((c) => (c.name || "").trim()),
      box_contents_label: (editing.box_contents_label || "").trim() || null,
      badge: editing.badge && editing.badge !== "None" ? editing.badge : null,
      featured: !!editing.featured,
      popularity: Number(editing.popularity) || 50,
      meta_title: editing.meta_title || null,
      meta_description: editing.meta_description || null,
      status: statusOverride || editing.status || "draft",
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdateProduct(editing.id, payload);
      else await adminCreateProduct(payload);
      toast.success(editing.id ? "Product updated" : "Product created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    try { await adminDeleteProduct(p.id); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const preview = (p) => window.open(`/diwali-collection/${p.slug}`, "_blank");

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-products-page">
      <AdminHeader active="products" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Catalogue</div>
            <h1 className="admin-heading">Products</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-product-btn">+ Add Product</button>
          )}
        </div>

        {editing ? (
          <ProductForm
            form={editing}
            collections={collections}
            saving={saving}
            onChange={setEditing}
            onSave={doSave}
            onCancel={cancel}
            onPreview={editing.id ? () => preview(editing) : null}
          />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-products-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No products yet. Click "Add Product" to create one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Product</th><th>Collection</th><th>Price</th><th>Badge</th><th>Status</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} data-testid={`prod-row-${p.slug}`}>
                      <td><strong>{p.name}</strong><div style={{ fontSize: 11, opacity: 0.5 }}>{p.slug}</div></td>
                      <td>{p.collection_slug || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td>₹{Number(p.price || 0).toLocaleString("en-IN")}</td>
                      <td>{p.badge || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td><span className={`admin-badge admin-badge-${p.status}`}>{p.status}</span></td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(p)} data-testid={`prod-edit-${p.slug}`}>Edit</button>
                        <button className="admin-link-btn" onClick={() => preview(p)} data-testid={`prod-preview-${p.slug}`}>Preview</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(p)} data-testid={`prod-del-${p.slug}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function ProductForm({ form, collections, saving, onChange, onSave, onCancel, onPreview }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  const toggleRecipient = (v) => {
    const s = new Set(form.recipient); s.has(v) ? s.delete(v) : s.add(v);
    set("recipient", Array.from(s));
  };
  const addContent = () => set("contents", [...form.contents, { name: "", description: "", icon_url: null }]);
  const removeContent = (i) => set("contents", form.contents.filter((_, idx) => idx !== i));
  const setContent = (i, k, v) => set("contents", form.contents.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  // Bulk paste — parses a pasted block of hamper contents (one per line),
  // stripping numbering, bullet markers, and extra whitespace. Appends new
  // rows to whatever is already in the contents list, preserving order.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const parseBulk = (raw) =>
    (raw || "")
      .split(/\r?\n/)
      .map((line) =>
        line
          .replace(/^[\s\u2022\-*·•●◦▪▫→>»]+/, "")   // leading bullets / dashes
          .replace(/^\(?\d+[.)\]]\s*/, "")            // 1. / 1) / (1) / 1]
          .replace(/^[a-zA-Z][.)]\s+/, "")            // a. / A) list markers
          .trim()
      )
      .filter(Boolean);
  const applyBulk = () => {
    const items = parseBulk(bulkText);
    if (items.length === 0) { setBulkOpen(false); return; }
    // Drop any leading empty row so pasted rows appear cleanly.
    const base = form.contents.filter((c) => (c.name || "").trim() || (c.description || "").trim());
    const additions = items.map((name) => ({ name, description: "", icon_url: null }));
    set("contents", [...base, ...additions]);
    setBulkText("");
    setBulkOpen(false);
  };
  const moveContent = (from, to) => {
    if (to < 0 || to >= form.contents.length) return;
    const arr = [...form.contents]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x); set("contents", arr);
  };

  const uploadImages = async (files) => {
    const arr = Array.from(files || []);
    for (const f of arr) {
      try { const { url } = await adminUploadImage(f); set("images", [...form.images, url]); }
      catch { toast.error(`Upload failed: ${f.name}`); }
    }
  };
  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));
  const moveImage = (from, to) => {
    if (to < 0 || to >= form.images.length) return;
    const arr = [...form.images]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x); set("images", arr);
  };

  const boxContentsCount = form.contents.filter((c) => (c.name || "").trim()).length;

  return (
    <div className="admin-form" data-testid="admin-product-form">
      <FormSection title="1 · Basic Information">
        <div className="admin-form-row">
          <label>Product Name<span className="req">*</span></label>
          <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => { if (!form.slug) set("slug", slugify(form.name)); }} data-testid="prod-form-name" />
        </div>
        <div className="admin-form-row">
          <label>URL Slug</label>
          <input type="text" value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="auto from name" data-testid="prod-form-slug" />
          <div className="admin-form-hint">Public URL: /diwali-collection/{form.slug || "your-slug"}</div>
        </div>
        <div className="admin-form-row">
          <label>Short Description</label>
          <textarea rows={3} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="prod-form-desc" />
        </div>
        <div className="admin-form-row">
          <label>Price / Bulk Price (₹)<span className="req">*</span></label>
          <input type="number" min={0} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="prod-form-price" />
        </div>
        <div className="admin-form-row">
          <label>MRP (₹) <span style={{ opacity: 0.55, fontWeight: 400 }}>— optional, shown on catalogue</span></label>
          <input type="number" min={0} value={form.mrp ?? ""} onChange={(e) => set("mrp", e.target.value)} data-testid="prod-form-mrp" />
        </div>
      </FormSection>

      <FormSection title="2 · Collection">
        <div className="admin-form-row">
          <label>Assigned Collection<span className="req">*</span></label>
          <select value={form.collection_slug} onChange={(e) => set("collection_slug", e.target.value)} data-testid="prod-form-collection">
            <option value="">— Select —</option>
            {collections.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>)}
          </select>
        </div>
      </FormSection>

      <FormSection title="3 · Product Images">
        <div className="admin-form-row">
          <label>Upload Images (multiple)</label>
          <input type="file" accept="image/*" multiple onChange={(e) => uploadImages(e.target.files)} data-testid="prod-form-images" />
          <div className="admin-form-hint">First image is the cover / hero. Drag to reorder using the arrows below.</div>
        </div>
        {form.images.length > 0 && (
          <div className="admin-image-grid">
            {form.images.map((src, i) => (
              <div key={`${src}-${i}`} className="admin-image-tile" data-testid={`prod-img-${i}`}>
                {i === 0 && <span className="admin-cover-tag">Cover</span>}
                <img src={assetUrl(src)} alt="" />
                <div className="admin-image-controls">
                  <button type="button" onClick={() => moveImage(i, i - 1)} disabled={i === 0}>←</button>
                  <button type="button" onClick={() => moveImage(i, i + 1)} disabled={i === form.images.length - 1}>→</button>
                  <button type="button" className="admin-link-danger" onClick={() => removeImage(i)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </FormSection>

      <FormSection title="4 · Product Details">
        <div className="admin-form-row">
          <label>Recipient (multi-select)</label>
          <div className="admin-chip-row">
            {RECIPIENTS.map((r) => (
              <label key={r.value} className={`admin-chip ${form.recipient.includes(r.value) ? "active" : ""}`}>
                <input type="checkbox" checked={form.recipient.includes(r.value)} onChange={() => toggleRecipient(r.value)} data-testid={`prod-form-recipient-${r.value}`} />
                {r.label}
              </label>
            ))}
          </div>
        </div>
        <div className="admin-form-row">
          <label>Availability</label>
          <select value={form.availability} onChange={(e) => set("availability", e.target.value)} data-testid="prod-form-availability">
            {AVAILABILITY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="admin-form-row">
          <label>MOQ (units)</label>
          <input type="number" min={1} value={form.moq} onChange={(e) => set("moq", e.target.value)} data-testid="prod-form-moq" />
        </div>
        <div className="admin-form-row">
          <label>Box Contents (label shown on product page)</label>
          <input
            type="text"
            value={form.box_contents_label || ""}
            onChange={(e) => set("box_contents_label", e.target.value)}
            placeholder={`e.g. ${boxContentsCount} Premium Curated Products`}
            data-testid="prod-form-box-contents-label"
          />
          <div className="admin-form-hint" data-testid="prod-form-box-count">
            Leave blank to auto-show "{boxContentsCount} {boxContentsCount === 1 ? "Item" : "Items"}" from section 5.
          </div>
        </div>
      </FormSection>

      <FormSection title="5 · Inside the Hamper (Contents)">
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="admin-nav-btn"
            onClick={() => setBulkOpen(true)}
            data-testid="prod-form-bulk-paste-btn"
          >
            Paste Multiple Items
          </button>
        </div>
        {bulkOpen && (
          <div className="admin-form-row" style={{ marginBottom: 16, padding: 16, background: "#FDF6EE", border: "1px solid #EDD9C0", borderRadius: 8 }} data-testid="prod-form-bulk-panel">
            <label>Paste one item per line — numbering and bullets are removed automatically</label>
            <textarea
              rows={10}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="4700BC Netflix Popcorn Tin&#10;True Gesture Almonds 75g&#10;True Gesture Cashews 75g&#10;Art of Puja Incense Sticks"
              data-testid="prod-form-bulk-textarea"
              autoFocus
            />
            <div className="admin-form-hint">
              {parseBulk(bulkText).length} item(s) detected · descriptions left blank · pasted order preserved
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button type="button" className="admin-nav-btn"
                onClick={() => { setBulkText(""); setBulkOpen(false); }}
                data-testid="prod-form-bulk-cancel">Cancel</button>
              <button type="button" className="admin-export-btn"
                onClick={applyBulk}
                disabled={parseBulk(bulkText).length === 0}
                data-testid="prod-form-bulk-import">Import</button>
            </div>
          </div>
        )}
        {form.contents.map((c, i) => (
          <div key={i} className="admin-repeater-row" data-testid={`prod-form-content-${i}`}>
            <div className="admin-repeater-fields">
              <input type="text" placeholder="Item name" value={c.name} onChange={(e) => setContent(i, "name", e.target.value)} data-testid={`content-name-${i}`} />
              <input type="text" placeholder="Short description (optional)" value={c.description || ""} onChange={(e) => setContent(i, "description", e.target.value)} />
            </div>
            <div className="admin-repeater-controls">
              <button type="button" onClick={() => moveContent(i, i - 1)} disabled={i === 0}>↑</button>
              <button type="button" onClick={() => moveContent(i, i + 1)} disabled={i === form.contents.length - 1}>↓</button>
              <button type="button" className="admin-link-danger" onClick={() => removeContent(i)}>×</button>
            </div>
          </div>
        ))}
        <button type="button" className="admin-nav-btn" onClick={addContent} data-testid="prod-form-add-content">+ Add Item</button>
      </FormSection>

      <FormSection title="6 · Product Badge">
        <div className="admin-form-row">
          <label>Badge</label>
          <select value={form.badge || "None"} onChange={(e) => set("badge", e.target.value === "None" ? "" : e.target.value)} data-testid="prod-form-badge">
            {BADGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="admin-form-row">
          <label>Featured</label>
          <label className="admin-chip">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="prod-form-featured" />
            Bubble to top of "Featured" sort
          </label>
        </div>
        <div className="admin-form-row">
          <label>Popularity Rank</label>
          <input type="number" min={0} max={1000} value={form.popularity} onChange={(e) => set("popularity", e.target.value)} data-testid="prod-form-popularity" />
          <div className="admin-form-hint">Higher = ranks higher in "Most Popular" sort.</div>
        </div>
      </FormSection>

      <FormSection title="7 · SEO (Optional)">
        <div className="admin-form-row">
          <label>Meta Title</label>
          <input type="text" value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} placeholder={`Auto: ${form.name || "Product Name"} · Utsah`} data-testid="prod-form-meta-title" />
        </div>
        <div className="admin-form-row">
          <label>Meta Description</label>
          <textarea rows={2} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} placeholder={`Auto: ${form.short_description?.slice(0, 140) || "Uses short description if left blank"}`} data-testid="prod-form-meta-desc" />
        </div>
      </FormSection>

      <FormSection title="8 · Status">
        <div className="admin-form-row">
          <label>Publish Status</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="prod-form-status">
            <option value="draft">Draft (hidden from public site)</option>
            <option value="published">Published (live on public site)</option>
          </select>
        </div>
      </FormSection>

      <div className="admin-form-actions" data-testid="prod-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="prod-form-cancel">Cancel</button>
        {onPreview && (
          <button className="admin-nav-btn" onClick={onPreview} disabled={saving} data-testid="prod-form-preview">Preview</button>
        )}
        <button className="admin-nav-btn" onClick={() => onSave("draft")} disabled={saving} data-testid="prod-form-save-draft">Save Draft</button>
        <button className="admin-export-btn" onClick={() => onSave("published")} disabled={saving} data-testid="prod-form-publish">
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="admin-form-section">
      <h3 className="admin-form-section-title">{title}</h3>
      {children}
    </section>
  );
}
